// A region of the source image in its natural pixels.
export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

// pl-back's saveFile keeps the uploaded size only for names with this marker.
const CROP_MARKER = ".crop";

const STORED_UUID_PREFIX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\./i;

const UNIQUE_SUFFIX = /-[a-z0-9]{6}$/;

// "uuid.photo-x1y2z3.crop.webp.webp" (stored) / "photo.png" -> "photo"
const baseName = (name: string) =>
  name
    .replace(STORED_UUID_PREFIX, "")
    .replace(/(\.[a-z0-9]+)+$/i, "")
    .replace(UNIQUE_SUFFIX, "") || "image";

// pl-back pairs uploads with fields by name, so two crops of "1.png" must not collide.
const uniqueSuffix = () => `-${Math.random().toString(36).slice(2, 8).padEnd(6, "0")}`;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image could not be loaded"));
    image.src = src;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas export failed"))),
      type,
      quality
    )
  );

export const renderCroppedFile = async (params: {
  src: string;
  area: Area;
  width: number;
  height: number;
  sourceName: string;
}): Promise<File> => {
  const image = await loadImage(params.src);

  const canvas = document.createElement("canvas");
  canvas.width = params.width;
  canvas.height = params.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is not supported");

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    image,
    params.area.x,
    params.area.y,
    params.area.width,
    params.area.height,
    0,
    0,
    params.width,
    params.height
  );

  // Browsers that can't encode webp silently return PNG, hence blob.type.
  const blob = await canvasToBlob(canvas, "image/webp", 0.92);
  const extension = blob.type === "image/webp" ? "webp" : "png";
  const name = `${baseName(params.sourceName)}${uniqueSuffix()}${CROP_MARKER}.${extension}`;

  const file = new File([blob], name, { type: blob.type });
  // pl-back matches uploads to fields by rawFile.path, which only react-dropzone sets.
  Object.defineProperty(file, "path", { value: name, enumerable: true });

  return file;
};

export const isCroppedFileName = (name: string | undefined) =>
  !!name && /\.crop\.(webp|png)$/i.test(name);

// no-store: a thumbnail cached from a non-CORS <img> request would fail the CORS check.
export const fetchAsObjectUrl = async (url: string) => {
  const response = await fetch(url, { mode: "cors", cache: "no-store" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return URL.createObjectURL(await response.blob());
};
